import Colors from "../config/colors";


/**
 * Determines whether a valid connection exists between two clusterlets.
 *
 * Rules:
 * 1. If either clusterlet has subtype "controller", connection is always valid.
 * 2. Otherwise, checks if a connection exists in either direction.
 *
 * @param {Object} params
 * @param {Array<Object>} params.connections - List of connection objects
 * @param {string|number} params.sourceId - Source clusterlet ID
 * @param {string|number} params.targetId - Target clusterlet ID
 * @param {Array<Object>} params.clusterlets - List of clusterlets
 *
 * @returns {boolean}
 */
export const doesConnectionExist = ({ connections = [], sourceId, targetId, clusterlets = [], } = {}) => {
  if (!sourceId || !targetId) return false;

  const normalizedSourceId = String(sourceId);
  const normalizedTargetId = String(targetId);

  // Build O(1) lookup map
  const clusterletMap = new Map(
    clusterlets.map(({ _id, ...rest }) => [String(_id), { _id, ...rest }])
  );
  const sourceClusterlet = clusterletMap.get(normalizedSourceId);
  const targetClusterlet = clusterletMap.get(normalizedTargetId);

  // Rule 1: Controller subtype allows auto-connection
  if ( sourceClusterlet?.subType === "controller"  ||  targetClusterlet?.subType === "controller" ) {
    return true;
  }

  // Rule 2: Check bidirectional connection existence
  return connections.some(({ source, target }) => {
    return (
      (String(source) === normalizedSourceId &&
        String(target) === normalizedTargetId) ||
      (String(source) === normalizedTargetId &&
        String(target) === normalizedSourceId)
    );
  });
};


/**
 * Calculates the appropriate gap between nodes based on the container width.
 *
 * @param {number} containerWidth - The total available width of the container.
 * @param {number} defaultGap - The default spacing between nodes.
 * @returns {number} - The adjusted gap between nodes.
 */
export const getGapBetweenNodes = (containerWidth, defaultGap) => {
  const edgePadding = 75; // Space around the first and last nodes
  const nodeCount = 4;    // Assuming we want to space 5 nodes
  const requiredWidth = defaultGap * nodeCount + edgePadding;

  if (requiredWidth > containerWidth) {
    return 300  ; // Fallback spacing when space is limited
  }

  return defaultGap;
};


/**
 * Calculates the required width for the canvas (paper) based on the number of nodes and spacing.
 *
 * @param {Object} params
 * @param {number} params.nodeCount - The number of nodes to render.
 * @param {number} params.gapBetweenNodes - The horizontal gap between each node.
 * @param {number} params.containerWidth - The available width of the container.
 * @returns {number|string} - Returns '100%' if the required width fits inside the container, otherwise returns the calculated width.
 */
export const calculatePaperWidth = ({ nodeCount=4, gapBetweenNodes, containerWidth, edgePadding=100 }) => {
  const requiredWidth = (nodeCount - 1) * gapBetweenNodes + edgePadding * 2;

  return requiredWidth < containerWidth ? containerWidth : requiredWidth;
};



/**
 * Calculates the height required for rendering the paper (canvas) based on the number of elements,
 * their individual height, offsets, and optional icon space.
 *
 * The function ensures that the paper height is never less than the container height.
 *
 * @param {number} containerHeight - Minimum height the paper should have (usually viewport or container height).
 * @param {number} verticalElementsCount - Number of vertical elements to render.
 * @param {number} singleElementHeight - Height of a single element.
 * @param {number} nodeVerticalLineOffset - Offset space (usually for alignment/spacing of node lines).
 * @param {number} createElementIconHeight - (Optional) Height of the icon to create a new element.
 * @param {number} createElementIconOffset - (Optional) Offset below the create icon for spacing.
 * @returns {number} Final computed height of the paper.
 */
export const calculatePaperHeight = ({
  containerHeight,
  verticalElementsCount,
  singleElementHeight,
  nodeVerticalLineOffset,
  createElementIconHeight = 0,
  createElementIconOffset = 0
}) => {
  const requiredHeight =
    (verticalElementsCount * singleElementHeight) +
    nodeVerticalLineOffset +
    createElementIconHeight +
    createElementIconOffset;

  return Math.max(requiredHeight, containerHeight);
};


export  const generateName = (items, type) => {
  const prefix = type === 'event' ? 'Event_' : 'Transaction_';

  const usedNumbers = items
    .filter(item => item.label && item.label.startsWith(prefix))
    .map(item => {
      const num = parseInt(item.label.replace(prefix, ''), 10);
      return isNaN(num) ? 0 : num;
    });

  const nextNumber = usedNumbers.length ? Math.max(...usedNumbers) + 1 : 1;

  return `${prefix}${nextNumber}`;
}


export const updateGroupYPosition = ({eventData, newY, boxHeight, animate = false, Raphael}) => {
  try {
    
    eventData.group.forEach(el => {
      const type = el.type;
      if (type === "rect" || type === "image") {
        const isExpandButton = el.data('isExpandButton');
        const x = el.attr("x");
        const nodeId = el.node.id?.split("_")
        const nodeCommonId = nodeId?.[nodeId.length - 1];
        let elNewY;
        if(["verticalDragHandle", "onsendHandlerRectangle", "expand","icon", "onrecvHandlerRectangle", "collapse", "expandWrapper"].includes(nodeCommonId )){
          let elHeight = el.attrs.height;
          elNewY = newY - (elHeight / 2)
        }else{
          elNewY = isExpandButton ? newY - boxHeight/2: newY - boxHeight; // 8 is the y difference of expand button with child transaction line y
        }
        if (animate) {
          el.animate({ x, y: elNewY }, 200, "easeInOut");
        } else {
          el.attr({ x, y: elNewY });
        }
      } else if (type === "text") {
        const x = el.attr("x");
        if (animate) {
          el.animate({ x, y: newY }, 200, "easeInOut");
        } else {
          el.attr({ x, y: newY });
        }
      } else if (type === "circle") {
        const cx = el.attr("cx");
        if (animate) {
          el.animate({ cx, cy: newY }, 200, "easeInOut");
        } else {
          el.attr({ cx, cy: newY });
        }
      } else if (type === "path" && ((el === eventData.eventArrowPath) || (el === eventData.transactionPath))) {

        const pathArray = Raphael.parsePathString(el.attr("path"));
        const updatedPath = pathArray.map(cmd => {
          if (cmd[0] === "M" || cmd[0] === "L") {
            return [cmd[0], cmd[1], newY];
          }
          return cmd;
        });
        if (animate) {
          el.animate({ path: updatedPath }, 200, "easeInOut");
        } else {
          el.attr({ path: updatedPath });
        }


      } else if (el === eventData.startShape) {
        const x = eventData.startX;
        const apex = eventData.apexLength;
        const base = eventData.baseHeight;
        const pathStr = `M${x},${newY - base} L${x},${newY + base} L${x - apex},${newY} Z`;

        if (animate) {
          el.animate({ path: pathStr }, 200, "easeInOut");
        } else {
          el.attr({ path: pathStr });
        }
      } else if (el === eventData.endShape) {
        const x = eventData.endX;
        const apex = eventData.apexLength;
        const base = eventData.baseHeight;
        const pathStr = `M${x},${newY - base} L${x},${newY + base} L${x + apex},${newY} Z`;

        if (animate) {
          el.animate({ path: pathStr }, 200, "easeInOut");
        } else {
          el.attr({ path: pathStr });
        }
      }
    });

    // Update internal Y tracker
    eventData.y = newY;


  } catch (error) {
    console.error(`Updating element group Y position failed. `, error);
    return null;
  }
}

export const updateElementYPosition = (eventData, newY, boxHeight, animate = false) => {
  try {
    
    eventData.group.forEach(el => {
      const type = el.type;

      if (type === "rect" || type === "image") {
        const x = el.attr("x");
        if (animate) {
          el.animate({ x, y: newY - boxHeight }, 200, "easeInOut");
        } else {
          el.attr({ x, y: newY - boxHeight });
        }

      } else if (type === "text") {
        const x = el.attr("x");
        if (animate) {
          el.animate({ x, y: newY }, 200, "easeInOut");
        } else {
          el.attr({ x, y: newY });
        }

      } else if (type === "circle") {
        const cx = el.attr("cx");
        if (animate) {
          el.animate({ cx, cy: newY }, 200, "easeInOut");
        } else {
          el.attr({ cx, cy: newY });
        }

      } else if (type === "path" && (el === eventData.eventArrowPath || el === eventData.transactionPath)) {
        const bbox = el.getBBox();
        const pathX = bbox.x;
        const pathWidth = bbox.width;
        const pathStr = `M${pathX},${newY} L${pathX + pathWidth},${newY}`;

        if (animate) {
          el.animate({ path: pathStr }, 200, "easeInOut");
        } else {
          el.attr({ path: pathStr });
        }

      } else if (el === eventData.startShape) {
        const x = eventData.startX;
        const apex = eventData.apexLength;
        const base = eventData.baseHeight;
        const pathStr = `M${x},${newY - base} L${x},${newY + base} L${x - apex},${newY} Z`;

        if (animate) {
          el.animate({ path: pathStr }, 200, "easeInOut");
        } else {
          el.attr({ path: pathStr });
        }

      } else if (el === eventData.endShape) {
        const x = eventData.endX;
        const apex = eventData.apexLength;
        const base = eventData.baseHeight;
        const pathStr = `M${x},${newY - base} L${x},${newY + base} L${x + apex},${newY} Z`;

        if (animate) {
          el.animate({ path: pathStr }, 200, "easeInOut");
        } else {
          el.attr({ path: pathStr });
        }
      }
    });

    // Update internal Y tracker
    eventData.y = newY;

  } catch (error) {
    console.error(`Updating element Y position failed. `, error);
    return null;
  }
}



const rebuildCategory = ({originalList, updatedList, type}) => {
  return updatedList
    .filter(u => originalList.some(orig => type==="invoked_transactions"? (orig.uuid === u.id) : (orig.id === u.id) ))
    .map(u => {
      let newObj = { id: u.id, order: u.order }

      if(type === "invoked_transactions"){
        const invokedItem = originalList?.find(orig => orig.uuid === u.id) || {};
        if(invokedItem){
          newObj = {...invokedItem, uuid: u.id, order: u.order } 
        }
      }

      return newObj;

    });
};


export const updateElementsOrder = ({selectedTransaction, draggableItems, verticalDragSpacing}) => {
  try {

    // spread operator to flatten the arrays
    const allOriginalItems = [ 
      ...(selectedTransaction.events || []),
      ...(selectedTransaction.nestedTransactions || []),
      ...(selectedTransaction.invokedTransactions || [])
    ];
    console.log({allOriginalItems})

    // Determine the base order from all original items
    const minOrder = allOriginalItems.reduce((min, item) => { 
      return item.order < min ? item.order : min;
    }, Infinity);

    const baseOrder = isFinite(minOrder) ? minOrder : 500; // default base order if no items exist

    const updatedOrderList = draggableItems
      .slice()
      .sort((a, b) => a.y - b.y)
      .map((item, index) => ({
        id: item.id,
        order: baseOrder + index * verticalDragSpacing
      }));
    console.log({updatedOrderList})

    const events = rebuildCategory({originalList: selectedTransaction.events || [], updatedList: updatedOrderList, type: "event"}, );
    const nestedTransactions = rebuildCategory({originalList: selectedTransaction.nestedTransactions || [], updatedList: updatedOrderList, type: "child_transactions"});
    const invokedTransactions = rebuildCategory({originalList: selectedTransaction.invokedTransactions || [], updatedList: updatedOrderList, type: "invoked_transactions"});
      

    return { events, nestedTransactions, invokedTransactions }


  } catch (error) {
    console.error(`Updating elements order failed. `, error);
    return null;
  }
}



/**
 * Updates the height of a vertical line element on the Raphael canvas.
 *
 * @param {Object} lineElement - The Raphael element representing the vertical line.
 * @param {number} newBottomY - The new bottom Y coordinate for the line.
 */
export const updateVerticalLineHeight = ({lineElement, newBottomY}) => {
  if (
    !lineElement ||
    typeof newBottomY !== 'number' ||
    lineElement.data("name") !== "vertical_line" ||
    !Array.isArray(lineElement.attrs?.path) ||
    lineElement.attrs.path.length < 2
  ) {
    console.warn("Invalid line element or path structure.");
    return;
  }

  const [moveCmd, lineCmd] = lineElement.attrs.path;

  const startX = moveCmd[1];
  const startY = moveCmd[2];

  const newPath = `M${startX},${startY} L${startX},${newBottomY}`;
  lineElement.attr({ path: newPath });
}



/**
 * Hides all circle and alert label elements from a given node's circle list.
 *
 * @param {Object} params
 * @param {Array} params.nodeCircle - An array of objects containing `circle` and optional `alertLabel`.
 */
export const hideAllCirclesOnNode =({ nodeCircle }) => {
  if (!Array.isArray(nodeCircle) || nodeCircle.length === 0) return;

  nodeCircle.forEach(item => {
    item?.circle?.hide();
    item?.alertLabel?.hide();
  });
}

/**
 * Hides UI elements related to an event on the canvas and changes the arrow stroke color.
 *
 * @param {Object} event - The event object containing visual Raphael elements.
 */
export const handleDraggingEventElements = (event) => {
  try {
    if (!event || typeof event !== 'object') {
      console.warn("Invalid event object provided to hideEventElements.");
      return;
    }

    const {
      onsendHandlerBox,
      onrecvHandlerBox,
      verticalDragHandler,
      labelRectangle,
      labelText,
      eventArrowPath,
      configAlertIcon,
      conditionAttributeIcon,
      conditionAttributeLabelBox,
      conditionATtributeLabel,
    } = event;

    onsendHandlerBox?.hide();
    onrecvHandlerBox?.hide();
    verticalDragHandler?.hide();
    labelRectangle?.hide();
    labelText?.hide();
    configAlertIcon?.hide();

    conditionAttributeIcon?.hide();
    conditionAttributeLabelBox?.hide();
    conditionATtributeLabel?.hide();


    if (eventArrowPath?.attr) {
      eventArrowPath.attr({ stroke: 'violet' });
    }

  } catch (error) {
    console.error("Handing dragging event elements failed. ", error);
    return null;
  }
};

/**
 * Removes circle and alert label elements from a list of node circle items.
 *
 * @param {Array} nodeCircles - Array of objects containing `circle` and optional `alertLabel` elements.
 */
export const removeNodeCircles = (nodeCircles) => {
  if (!Array.isArray(nodeCircles)) {
    console.warn("Expected nodeCircles to be an array.");
    return;
  }

  nodeCircles.forEach(item => {
    item?.circle?.remove();
    item?.alertLabel?.remove();
  });
};

/**
 * Resets an event's visual elements after a drag-and-drop interaction is completed.
 *
 * @param {Object} params
 * @param {Object} params.event - The event object with visual elements.
 * @param {number} params.dragHandlerX - The original X coordinate to restore the drag handler to.
 */
export const resetDraggingEventElementsOnIDrop = ({ event, dragHandlerX }) => {
  if (!event || typeof event !== 'object') {
    console.warn("Invalid event object passed to resetDraggingEventElementsOnIDrop.");
    return;
  }
  

  const {
    eventArrowPath,
    horizontalDragHandler,
    onsendHandlerBox,
    onrecvHandlerBox,
    verticalDragHandler,
    labelRectangle,
    labelText,
    lineStart,
    lineEnd,
    y
  } = event;

  // Reset event arrow path
  eventArrowPath?.attr({
    path: `M${lineStart},${y} L${lineEnd},${y}`,
    stroke: Colors.dataflow.arrowStroke,
  });

  // Reset drag handler
  horizontalDragHandler?.attr({
    x: dragHandlerX,
    fill: '#fff',
  });
  horizontalDragHandler?.hide();

  // Show other UI elements
  onsendHandlerBox?.show();
  onrecvHandlerBox?.show();
  verticalDragHandler?.show();
  labelRectangle?.show();
  labelText?.show();
};


/**
 * Returns a list of droppable nodes during an event drag operation,
 * excluding the current source and target, and marking if a connection exists.
 *
 * @param {Object} params
 * @param {Object} params.nodes - Map or object of all nodes (keyed by ID).
 * @param {Array} params.connections - List of existing connection objects.
 * @param {Object} params.event - The event being dragged (not used but may be useful later).
 * @param {Object} params.source - The source node of the current event.
 * @param {Object} params.target - The target node of the current event.
 * @param {Object} params.allClusterlets - List of all clusterlets.
 * @returns {Array} List of candidate nodes with connection status.
 */
export const getNewEventTargetsOnEventDrag = ({ nodes, connections, event, source, target, allClusterlets }) => {
  if (!nodes || typeof nodes !== 'object' || !Array.isArray(connections)) {
    console.warn("Invalid input to getNewEventTargetsOnEventDrag");
    return [];
  }

  const droppableNodes = Object.values(nodes).filter(
    (node) => node?.id !== source?.id && node?.id !== target?.id
  );

  return droppableNodes.map((node) => ({
    ...node,
    doesConnectionExist: doesConnectionExist({connections, sourceId: source?.id, targetId: node?.id, clusterlets: allClusterlets})
  }));
};



/**
 * Finds and displays the circle and optional alert label for a specific node-event pair.
 *
 * @param {Array} nodeCircles - Array of objects linking nodes and events to visual elements.
 * @param {string} nodeId - ID of the node to match.
 * @param {Object} event - Event object (should contain an `id`).
 */
export const showNodeCircleOnEventDrag = (nodeCircles, nodeId, event) => {
  if (!Array.isArray(nodeCircles) || !nodeId || !event?.id) {
    console.warn("Invalid parameters passed to showNodeCircle.");
    return;
  }

  const nodeCircle = nodeCircles.find(
    (item) => item?.event === event.id && item?.node === nodeId
  );

  nodeCircle?.circle.show();

  nodeCircle?.alertLabel?.show?.();
};



/**
 * Removes the SVG group containing the event arrow elements from the canvas.
 *
 * @param {Object} event - The event object that contains a Raphael group element.
 */
export const removeEventArrowElements = (event) => {
  if (!event?.group?.remove) {
    console.warn("Event group element is invalid or missing.");
    return;
  }

  event.group.remove();
};


/**
 * Renders a "No connection exists" alert label on the Raphael canvas.
 *
 * @param {Object} params
 * @param {number} params.x - X coordinate for the alert label.
 * @param {number} params.y - Y coordinate for the alert label (text will be offset below this).
 * @param {Object} params.paper - Raphael paper instance to render on.
 * @param {Object} params.labelBoxConfig - Configuration object containing label styles.
 * @returns {Object|null} - The created Raphael text element, or null if inputs are invalid.
 */
export const renderNoConnectionAlert = ({ x, y, paper, labelBoxConfig }) => {
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    !paper?.text ||
    typeof labelBoxConfig?.fontSize !== 'number'
  ) {
    console.warn("Invalid parameters passed to renderNoConnectionAlert.");
    return null;
  }

  const alertText = "No connection exist";

  const alertLabel = paper.text(x, y + 24, alertText).attr({
    "font-size": labelBoxConfig.fontSize,
    "text-anchor": "middle",
    "dominant-baseline": "middle",
    fill: "grey",
    // "font-weight": "600" // Uncomment if needed
  });

  alertLabel.toFront();
  return alertLabel;
};

/**
 * Renders a circle on a node to visually indicate connection status.
 *
 * @param {Object} paper - Raphael paper instance to draw on.
 * @param {number} x - X-coordinate for the center of the circle.
 * @param {number} y - Y-coordinate for the center of the circle.
 * @param {boolean} [doesConnectionExist=false] - Whether the node has an existing connection.
 * @returns {Object|null} - The Raphael circle element, or null if inputs are invalid.
 */
export const renderCircleOnNode = (paper, x, y, doesConnectionExist = false) => {
  if (!paper?.circle || typeof x !== 'number' || typeof y !== 'number') {
    console.warn("Invalid parameters passed to renderCircleOnNode.");
    return null;
  }

  const radius = 15;
  const strokeColor = doesConnectionExist ? '#058f06' : '#df3509'; // Green if connected, red otherwise

  return paper.circle(x, y, radius).attr({
    fill: "transparent",
    stroke: strokeColor,
    "stroke-width": 1,
    "stroke-dasharray": "-", // Dotted stroke
  });
};


/**
 * Finds a nearby node during drag based on bounding box proximity.
 *
 * @param {Object} params
 * @param {Object} params.draggingNode - The node currently being dragged.
 * @param {Object} params.allNodes - All nodes in the canvas (object map).
 * @param {Object} params.newNodePositions - New x-positions for nodes, keyed by node ID.
 * @param {number} [params.proximity=60] - Distance in pixels to consider a node as "nearby".
 * @returns {Object|undefined} - The nearby node object if found, otherwise undefined.
 */
export const getNearbyNodeOnDrag = ({ draggingNode, allNodes, newNodePositions, proximity = 60 }) => {
  if (!draggingNode || !draggingNode.nodeWrapperRect || !allNodes) {
    console.warn("Invalid input to getNearbyNodeOnDrag");
    return;
  }

  // Update node list with new positions (immutable)
  const updatedNodes = Object.values(allNodes).map(node => {
    const updatedX = newNodePositions?.[node.id];
    return updatedX !== undefined ? { ...node, x: updatedX } : node;
  });

  // Filter out the dragging node itself
  const candidateNodes = updatedNodes.filter(node => node?.id !== draggingNode?.id);

  const draggedBBox = draggingNode.nodeWrapperRect.getBBox();

  for (const candidate of candidateNodes) {
    const targetRect = candidate?.nodeWrapperRect;
    if (!targetRect?.getBBox) continue;

    const targetBBox = targetRect.getBBox();

    const isNearby =
      draggedBBox.x < targetBBox.x + targetBBox.width + proximity &&
      draggedBBox.x + draggedBBox.width > targetBBox.x - proximity &&
      draggedBBox.y < targetBBox.y + targetBBox.height + proximity &&
      draggedBBox.y + draggedBBox.height > targetBBox.y - proximity;

    if (isNearby) {
      return candidate;
    }
  }

  return undefined;
};


/**
 * Updates the X position of a vertical line (Raphael path element).
 *
 * @param {Object} params
 * @param {Object} params.lineElement - Raphael line element representing a vertical path.
 * @param {number} params.newX - The new X coordinate to align the vertical line to.
 * @param {string} [params.color=Colors.black] - Optional stroke color.
 * @returns {Object|null} The updated line element or null if invalid.
 */
  export const updateNodeVerticalLineXPosition = ({ lineElement, newX, color = Colors.black }) => {
    if (!lineElement?.attr?.("path")) {
      console.warn("Invalid or missing line element in updateNodeVerticalLineXPosition.");
      return null;
    }

    const path = lineElement.attr("path");

    // Validate and extract Y coordinates
    const y1 = path?.[0]?.[2];
    const y2 = path?.[1]?.[2];

    if (typeof y1 !== "number" || typeof y2 !== "number") {
      console.warn("Invalid path format in line element.");
      return null;
    }

    const updatedPath = [
      ["M", newX, y1],
      ["L", newX, y2]
    ];

    lineElement.attr({
      path: updatedPath,
      stroke: color,
    });

    return lineElement;
  };


/**
 * Generates a sorted list of clusterlets with updated order based on x-position.
 *
 * @param {Object} params
 * @param {Object} params.newNodePositions - Map of node ID to new x-coordinate.
 * @param {Object} params.nodes - Map of all nodes by ID.
 * @returns {Array} Array of clusterlet objects with `id` and updated `order`.
 */
export const generateClusterletsWithUpdatedOrder = ({ newNodePositions = {}, nodes = {} }) => {
  if (typeof newNodePositions !== 'object' || typeof nodes !== 'object') {
    console.warn("Invalid input passed to generateClusterletsWithUpdatedOrder.");
    return [];
  }

  // Clone nodes and apply updated x positions (immutably)
  const updatedNodes = Object.values(nodes).map(node => ({
    ...node,
    x: newNodePositions[node.id] ?? node.x,
  }));

  // Sort based on updated x positions
  const sortedNodes = updatedNodes.sort((a, b) => a.x - b.x);

  // Assign order index based on sorted position
  return sortedNodes.map((node, index) => ({
    id: node.id,
    order: index,
  }));
};

export const showAllHIddenElementOnStopDragging = () => {
      
  // show plus icons
  Object.values(this.nodes)?.forEach(node => {
    node?.plusCircle?.show();
    // node?.popupSet?.show();
  })

  // show Events
  Object.values(this.arrowManager?.eventArrows)?.forEach(event => {
    event?.group?.show();
  })


  // show child txns
  Object.values(this.arrowManager?.childTransactions)?.forEach(txn => {
    txn?.group?.items?.forEach(item => {
      item?.show();
    })
  })

  // show child txns
  Object.values(this.arrowManager?.invokedTransactions)?.forEach(txn => {
    txn?.group?.items?.forEach(item => {
      item?.show();
    })
  })

  // Hide delete, horizontal handler 
  Object.values(this.arrowManager?.eventArrows)?.forEach(item => {
    item?.deleteButton?.hide();
    item?.horizontalDragHandler?.hide();
  })
  Object.values(this.arrowManager?.childTransactions)?.forEach(item => {
    item?.deleteButton?.hide();
  })

  Object.values(this.arrowManager?.invokedTransactions)?.forEach(item => {
    item?.deleteButton?.hide();
  })

}


export const getElementsToRemoveOnDrag = (
  childTrId,
  transactions,
  events,
  draggableItems,
  expandedChildTransactions
) => {
  const elementsToRemove = [];

  const draggingChildTransaction = transactions.find(item => item._id.toString() === childTrId);
  if (!draggingChildTransaction) return elementsToRemove;
  if (draggingChildTransaction.nestedTransactions && Array.isArray(draggingChildTransaction.nestedTransactions)) {
    for (const child of draggingChildTransaction.nestedTransactions) {
      const draggableChildTransaction = draggableItems.find(item => item.id === child.id);
      if (draggableChildTransaction) elementsToRemove.push(draggableChildTransaction);
  
      if (expandedChildTransactions[child.id]) {
        const nestedElements = getElementsToRemoveOnDrag(
          child.id,
          transactions,
          events,
          draggableItems,
          expandedChildTransactions
        );
        elementsToRemove.push(...nestedElements);
      }
    }
  }
  if (draggingChildTransaction.events && Array.isArray(draggingChildTransaction.events)) {
    for (const event of draggingChildTransaction.events) {
      const draggableEvent = draggableItems.find(item => item.id === event.id);
      if (draggableEvent) elementsToRemove.push(draggableEvent);
    }
  }

  if (draggingChildTransaction.invokedTransactions && Array.isArray(draggingChildTransaction.invokedTransactions)) {
    for (const invokedTransaction of draggingChildTransaction.invokedTransactions) {
      const draggableInvokedTransaction = draggableItems.find(item => item.id === invokedTransaction.id);
      if (draggableInvokedTransaction) elementsToRemove.push(draggableInvokedTransaction);
    }
  }

  return elementsToRemove;
};

/**
 * Renders a diagonal stripe pattern for child transactions on the Raphael canvas.
 *
 * @param {Object} params
 * @param {Object} params.paper - The Raphael paper instance to render the pattern on.    
 * @param {string} [params.patternId="child-diagonalStripe"] - Optional ID for the pattern.
 * @returns {void}
 */  
export const renderDiagonalStripePatternForChildTransction = ({ paper, patternId = "child-diagonalStripe" }) => {
  const svg = paper.canvas;
  const backgroundColor = "#f0f8ff"

  if (document.getElementById(patternId)) return;

  const defs = svg.querySelector("defs") || (() => {
    const d = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    svg.insertBefore(d, svg.firstChild);
    return d;
  })();

  const pattern = document.createElementNS("http://www.w3.org/2000/svg", "pattern");
  pattern.setAttribute("id", patternId);
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  pattern.setAttribute("width", 10);
  pattern.setAttribute("height", 10);

  // 🌤️ Very light blue background
  const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  bg.setAttribute("width", "10");
  bg.setAttribute("height", "10");
  bg.setAttribute("fill", backgroundColor); // very light blue

  // ➖ Lightened diagonal stripe
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M0,10 L10,0");
  path.setAttribute("stroke", "#e0e0e0"); // softer gray
  path.setAttribute("stroke-width", "2");

  pattern.appendChild(bg);
  pattern.appendChild(path);
  defs.appendChild(pattern);
};

export const moveChildTransactionBackgroundToBack = ({childTxnElements}) => {
  childTxnElements?.forEach(child => {
    if(child?.firstLevelChild && child?.childWrapperRectangle){
      child?.childWrapperRectangle?.toBack();
    }
  })
}

/**
 * Recursively finds the maximum depth of nested children,
 * considering only 'nested' type nodes that are expanded.
 *
 * @param {Array} children - Array of child nodes.
 * @param {Object} expandedList - Object where expandedList[id] is truthy if the node is expanded.
 * @returns {number} - The maximum depth found in the nested structure.
 */
export const findDepth = (children = [], expandedList = {}) => {
  if (!Array.isArray(children) || children.length === 0) return 1;

  let maxChildDepth = 1;

  children.forEach(child => {
    const isNestedAndExpanded = child?.type === "nested" && expandedList?.[child.id] && Array.isArray(child.children)  ;

    if (isNestedAndExpanded) {
      const childDepth = findDepth(child.children, expandedList);
      maxChildDepth = Math.max(maxChildDepth, 1 + childDepth);
    }
  });

  return maxChildDepth;
};

/**
 * Hides all non-dragging UI elements like nodes, events, child transactions, and invoked transactions.
 *
 * @param {Object} params
 * @param {Object} params.allNodes - Map of all nodes.
 * @param {Object} params.allEvents - Map of all events.
 * @param {Object} params.allChildTxns - Map of all child transactions.
 * @param {Object} params.allInvokedTxns - Map of all invoked transactions.
 */
export const hideAllOtherElementsOnNodeDragging = ({
  allNodes = {},
  allEvents = {},
  allChildTxns = {},
  allInvokedTxns = {}
}) => {

  // Hide node UI elements
  Object.values(allNodes).forEach(node => {
    node?.plusCircle?.hide?.();
    node?.popupSet?.hide?.();
    // event?.group?.attr({ opacity: 0.4 });
  });

  // Hide events
  Object.values(allEvents).forEach(event => {
    event?.group?.hide?.();
  });

  // Hide child transactions
  Object.values(allChildTxns).forEach(txn => {
    txn?.group?.items?.forEach(item => item?.hide?.());
    txn?.childWrapperRectangle?.hide?.();
  });

  // Hide invoked transactions
  Object.values(allInvokedTxns).forEach(txn => {
    txn?.group?.items?.forEach(item => item?.hide?.());
  });
};

export const removeArrowGroups = async (arrowGroups, fadeDuration = 100) => {
  const allElements = [];

  for (const group of arrowGroups) {
    const elements = Array.isArray(group) ? group : group?.items || [];
    allElements.push(...elements);
  }

  if (!allElements.length) return;

  await Promise.all(
    allElements.map(el => {
      return new Promise(resolve => {
        if (el && el.animate) {
          el.animate({ opacity: 0 }, fadeDuration, "linear", () => {
            el.remove();
            resolve();
          });
        } else {
          resolve();
        }
      });
    })
  );
};

export const removeRectsFromBottomToTop = async (rectangles, duration = 100) => {
  if (!Array.isArray(rectangles)) return;

  const animations = rectangles.map(rect => {
    return new Promise(resolve => {
      if (!rect || typeof rect.getBBox !== "function") return resolve();

      const bbox = rect.getBBox();

      rect.animate(
        {
          height: 0,
          y: bbox.y + bbox.height,
          opacity: 0
        },
        duration,
        "easeIn",
        () => {
          rect.remove();
          resolve();
        }
      );
    });
  });

  await Promise.all(animations);
};

/**
 * Renders a background wrapper rectangle on a Raphael paper instance.
 *
 * @param {Object} params
 * @param {Object} params.paper - Raphael paper object.
 * @param {number} params.width - Width of the background.
 * @param {number} params.height - Height of the background.
 * @param {string} [params.id='background-wrapper'] - ID for the wrapper element.
 * @returns {Object} - The created Raphael element.
 */
export const renderBackgroundWrapperForPaper = ({
  paper,
  width=0,
  height=0,
  id = 'background-wrapper'
}) => {
  if (!paper || !width || !height) {
    console.error('Invalid parameters passed to renderBackgroundWrapperForPaper');
    return null;
  }

  const backgroundWrapper = paper.rect(0, 0, width, height).attr({
    fill: 'transparent',
    stroke: 'none',
    'stroke-width': 0,
  });

  backgroundWrapper.toBack();

  // Assign an ID to the DOM node for easy reference
  if (backgroundWrapper?.node) {
    backgroundWrapper.node.id = id;
  }

  return backgroundWrapper;
};

/**
 * Finds all parent IDs of a selected item up to the root transaction.
 *
 * @param {Object} params
 * @param {string} params.transactionId - The root transaction ID.
 * @param {Object} params.selectedItem - The currently selected item.
 * @param {Object} params.elementsList - Map of element IDs to element objects.
 * @returns {Array<string>} - An array of parent IDs in the hierarchy.
 */
export const findAllParents = ({ transactionId, selectedItem, elementsList }) => {
  if (!selectedItem?.parent || !elementsList) return [];

  const parentIDs = new Set(); // prevent duplicates in case of cycles

  const findParentById = (id) => {
    if (!id || parentIDs.has(id)) return; // stop if invalid or already visited
    parentIDs.add(id);

    const item = elementsList?.find(item => item?.id === id);
    if (item?.parent && item.parent !== transactionId) {
      findParentById(item.parent);
    }
  };

  findParentById(selectedItem.parent);

  return Array.from(parentIDs);
};


  // To enable the automatic scrollling the when the drag functionality is being performed to either side of the screen
  export const enableHorizontalAutoScrollOnDrag = (containerId = "transaction_container", threshold = 50, scrollSpeed = 2) => {
    const container = document.getElementById(containerId);
    if (!container) return;
  
    let scrollDelta = 0;
    let animationFrameId = null;
    let direction = null; // 'left' or 'right'
  
    const step = () => {
      if (direction === 'left') {
        container.scrollLeft -= scrollSpeed;
        scrollDelta -= scrollSpeed;
      } else if (direction === 'right') {
        container.scrollLeft += scrollSpeed;
        scrollDelta += scrollSpeed;
      }
      animationFrameId = requestAnimationFrame(step);
    };
  
    const handleDragMove = (e) => {
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX;
  
      // Determine scroll direction
      if (mouseX < containerRect.left + threshold) {
        if (direction !== 'left') {
          direction = 'left';
          cancelAnimationFrame(animationFrameId);
          animationFrameId = requestAnimationFrame(step);
        }
      } else if (mouseX > containerRect.right - threshold) {
        if (direction !== 'right') {
          direction = 'right';
          cancelAnimationFrame(animationFrameId);
          animationFrameId = requestAnimationFrame(step);
        }
      } else {
        // Stop scrolling when mouse moves away from edges
        direction = null;
        cancelAnimationFrame(animationFrameId);
      }
    };
  
    const getScrollDelta = () => scrollDelta;
  
    const resetScroll = () => {
      direction = null;
      cancelAnimationFrame(animationFrameId);
      scrollDelta = 0;
    };
  
    return { handleDragMove, resetScroll, getScrollDelta };
  }

  // To enable vertical auto scrolling during the veritical event reordering 
  export const enableVerticalAutoScrollOnDrag = (containerId="transaction_container", threshold = 50, scrollSpeed = 2) => {
    const container = document.getElementById(containerId);
    if (!container) return;

    let scrollDelta = 0;
    let animationFrameId = null;
    let direction = null; // 'up' or 'down'

    const step = () => {
      if (direction === 'up') {
        container.scrollTop -= scrollSpeed;
        scrollDelta -= scrollSpeed;
      } else if (direction === 'down') {
        container.scrollTop += scrollSpeed;
        scrollDelta += scrollSpeed;
      }
      animationFrameId = requestAnimationFrame(step);
    };

    const handleDragMove = (e) => {
      const containerRect = container.getBoundingClientRect();
      const mouseY = e.clientY;

      // Determine scroll direction
      if (mouseY < containerRect.top + threshold) {
        if (direction !== 'up') {
          direction = 'up';
          cancelAnimationFrame(animationFrameId);
          animationFrameId = requestAnimationFrame(step);
        }
      } else if (mouseY > containerRect.bottom - threshold) {
        if (direction !== 'down') {
          direction = 'down';
          cancelAnimationFrame(animationFrameId);
          animationFrameId = requestAnimationFrame(step);
        }
      } else {
        // Stop scrolling when mouse moves away from edges
        direction = null;
        cancelAnimationFrame(animationFrameId);
      }
    };

    const getScrollDelta = () => scrollDelta;

    const resetScroll = () => {
      direction = null;
      cancelAnimationFrame(animationFrameId);
      scrollDelta = 0;
    };

    return { handleDragMove, resetScroll, getScrollDelta };
  }


  export const renderStyledTooltip = () => {
      let tooltip = document.getElementById("styled-raphael-tooltip");
      if (!tooltip) {
        tooltip = document.createElement("div");
        tooltip.id = "styled-raphael-tooltip";
    
        // Main tooltip box styles
        Object.assign(tooltip.style, {
          position: "absolute",
          background: "#fff",
          color: "#000",
          padding: "6px 10px",
          borderRadius: "6px",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
          fontSize: "13px",
          fontWeight: "bold",
          pointerEvents: "none",
          display: "none",
          zIndex: "9999",
          whiteSpace: "nowrap",
          transform: "translateX(-50%)",
        });
    
        // Tooltip arrow div
        const arrow = document.createElement("div");
        arrow.id = "styled-raphael-tooltip-arrow";
        Object.assign(arrow.style, {
          position: "absolute",
          top: "100%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "0",
          height: "0",
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: "6px solid #fff",
        });
    
        tooltip.appendChild(arrow);
        document.body.appendChild(tooltip);
      }
    
      return tooltip;
  }

  // Updates the X position of a node label wrapper and its multiline text safely during drag operations.
  export const updateNodeLabelXPosition = ({ nodeLabelWrapper, nodeLabelText, newX }) => {
    // Move rectangle
    const rectWidth = nodeLabelWrapper.getBBox().width;
    nodeLabelWrapper.attr({
      x: newX - rectWidth / 2
    });
  
    // ✅ Move only tspans
    const tspans = nodeLabelText.node.querySelectorAll("tspan");
    tspans.forEach(tspan => {
      tspan.setAttribute("x", newX);
    });
  };


  // This function renders a center-aligned rectangular label with bold, multiline text inside it using Raphael
  // - Automatically wraps long text into multiple lines
  // - Aligns text to the bottom of the rectangle
  // - Uses absolute positioning to prevent text drift during drag
  // - Returns both the rectangle and text elements so they can be moved together
  export const renderNodeLabelWithWrapper = ({ paper, x, y, text }) => {
    if (!paper) return null;
  
    const width = 170;
    const height = 40;
    const radius = 4;
    const padding = 6;
  
    const fontSize = 14;
    const lineHeight = fontSize * 1.25;
  
    const rectX = x - width / 2;
    const rectY = y;
  
    // Rectangle (transparent border)
    const rect = paper.rect(rectX, rectY, width, height, radius).attr({
      fill: "transparent",
      stroke: "transparent",
      "stroke-width": 2
    });
  
    // Text container (position handled via tspans)
    const textEl = paper.text(0, 0, "").attr({
      "font-size": fontSize,
      "font-weight": "bold",
      "text-anchor": "middle",
      fill: "#000"
    });
  
    textEl.node.setAttribute("id", `node_${text}_labelText`);
  
    const maxWidth = width - padding * 2;
  
    // Canvas for accurate text measurement
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.font = `bold ${fontSize}px Arial`;
  
    // Tokenize (snake_case + camelCase)
    const tokenize = (str) => {
      const tokens = [];
      let buffer = "";
  
      for (let i = 0; i < str.length; i++) {
        const char = str[i];
        const prev = str[i - 1];
  
        if (char === "_") {
          buffer += char;
          tokens.push(buffer);
          buffer = "";
          continue;
        }
  
        if (prev && /[a-z0-9]/.test(prev) && /[A-Z]/.test(char)) {
          tokens.push(buffer);
          buffer = char;
          continue;
        }
  
        buffer += char;
      }
  
      if (buffer) tokens.push(buffer);
      return tokens;
    };
  
    const tokens = tokenize(text);
    const lines = [];
    let currentLine = "";
  
    tokens.forEach(token => {
      const testLine = currentLine + token;
      if (ctx.measureText(testLine).width <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = token;
        } else {
          // Hard wrap long token
          let chunk = "";
          for (const ch of token) {
            const testChunk = chunk + ch;
            if (ctx.measureText(testChunk).width <= maxWidth) {
              chunk = testChunk;
            } else {
              lines.push(chunk);
              chunk = ch;
            }
          }
          if (chunk) lines.push(chunk);
          currentLine = "";
        }
      }
    });
  
    if (currentLine) lines.push(currentLine);
  
    // Apply multiline tspans (bottom-aligned, absolute Y)
    const node = textEl.node;
    while (node.firstChild) node.removeChild(node.firstChild);
  
    const baseY = rectY + height - padding;
    const startY = baseY - (lines.length - 1) * lineHeight;
  
    lines.forEach((line, i) => {
      const tspan = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "tspan"
      );
  
      tspan.setAttribute("x", x);
      tspan.setAttribute("y", startY + i * lineHeight);
      tspan.textContent = line;
  
      node.appendChild(tspan);
    });
  
    return {
      nodeLabelWrapper: rect,
      nodeLabelText: textEl
    };
  };
  
  // To remove the vertical drag handler, horizontal drag handler from events, child txns and invoked txns during element vertical drag
  export const removeDragHandlerFromEventAndChildTxn = ({entityId, events, childTxns, invokedTxns}) => {
    try {
      // Events
      events.forEach(eventItem => {
        eventItem?.deleteButton?.forEach(buttonElement => buttonElement.remove());
        eventItem?.messageButton?.forEach(buttonElement => buttonElement.remove());
        eventItem?.horizontalDragHandler?.remove();
      })

      // Child txns 
      childTxns.forEach(childItem => {
        childItem?.deleteButton?.forEach(buttonElement => buttonElement.remove());
      })

      // Invoked txns
      invokedTxns.forEach(invokedItem => {
        invokedItem?.deleteButton?.forEach(buttonElement => buttonElement.remove());
      })


    } catch (error) {
      console.error(`Remving drag handlers from events, child txns and invoked txns failed. `,error);
      return null;
    }
  }